/**
 * The menu.
 *
 * WHY EDITING IS SOMETIMES REFUSED HERE
 * -------------------------------------
 * The menu is decided centrally and travels down to each branch. A pull
 * writes the cloud's copy over the local one column by column, so a price
 * changed at a branch would be accepted, saved, shown back, used for the rest
 * of the day — and quietly reverted at the next sync. Nothing would error.
 *
 * So where this machine receives its menu from somewhere else, the screen
 * says so and does not offer controls that cannot work. Where it does not —
 * a single restaurant, or head office itself — everything is editable,
 * because nothing is going to overwrite it.
 *
 * WHY WITHDRAWN DISHES ARE SHOWN
 * ------------------------------
 * A dish taken off the menu used to disappear from this list, and nothing
 * could put it back: a seasonal item removed in winter was gone for good.
 * They are listed here, greyed, with a way to restore them. The till still
 * only sees what is on sale.
 */
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatPKR } from '@foodrun/shared';

import { api, withQuery } from '../../lib/api.js';
import { useAuth } from '../../stores/auth.js';
import { Tabs } from '../../components/ui/Tabs.jsx';
import { DataTable } from '../../components/ui/DataTable.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { OffersTab } from './OffersTab.jsx';
import { ChoicesTab } from './ChoicesTab.jsx';
import { DishChoices } from './DishChoices.jsx';
import { ImagePicker } from './ImagePicker.jsx';
import { IngredientForm } from './IngredientForm.jsx';
import { RecipeForm } from './RecipeForm.jsx';
import { describeRules } from './choiceRules.js';
import { ChoicePicker } from '../pos/ChoicePicker.jsx';

export default function MenuPage() {
  const [tab, setTab] = useState('products');
  const [editing, setEditing] = useState(null);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [editingChoices, setEditingChoices] = useState(null);

  const queryClient = useQueryClient();

  /**
   * Whether a change made here would survive.
   *
   * The same status the sync indicator reads. A branch that pulls its menu
   * from a cloud does not own it.
   */
  const sync = useQuery({
    queryKey: ['sync', 'status'],
    queryFn: () => api.get('/sync/status'),
    staleTime: 60_000,
  });

  const receivesMenu = sync.data?.cloud?.configured === true;

  /**
   * Two separate questions, and both have to be yes.
   *
   * Whether this branch owns its menu at all, and whether the person looking
   * at it is allowed to change this particular thing. They used to be one
   * question — the sync one — so a cashier saw every edit button on the screen
   * and got a refusal from the server when they pressed one.
   *
   * That went unnoticed until the recipe editor arrived: a cashier could open
   * a recipe, change the quantities, watch the food cost move, press save and
   * be told no. Showing somebody a door they cannot walk through wastes their
   * time and their trust in the screen.
   *
   * Recipes are a separate permission from the rest of the menu, held by the
   * people who look after stock rather than by whoever writes the menu, so it
   * is asked about separately.
   */
  const branchOwnsIt = sync.isSuccess && !receivesMenu;
  const can = useAuth((s) => s.can);

  const canEdit = branchOwnsIt && can('menu.update.menu');
  const canEditRecipes = branchOwnsIt && can('recipe.update.recipe');

  const products = useQuery({
    queryKey: ['menu', 'products', 'all'],
    // Withdrawn dishes included, so they can be put back. The till uses the
    // same endpoint without this and sees only what is on sale.
    queryFn: () => api.get('/menu/products?include_inactive=1'),
    staleTime: 5 * 60_000,
  });

  const categories = useQuery({
    queryKey: ['menu', 'categories'],
    queryFn: () => api.get('/menu/categories'),
    staleTime: 5 * 60_000,
  });

  const offers = useQuery({
    queryKey: ['menu', 'promotions'],
    queryFn: () => api.get('/menu/promotions'),
    staleTime: 5 * 60_000,
  });

  /**
   * What each dish costs to make, beside what it sells for.
   *
   * The only question a manager opens this for is which dishes are eating the
   * margin, so the answer is a column rather than something to work out.
   */
  const recipes = useQuery({
    queryKey: ['menu', 'recipes'],
    queryFn: () => api.get('/menu/recipes'),
  });

  const ingredients = useQuery({
    queryKey: ['menu', 'ingredients'],
    queryFn: () => api.get('/menu/ingredients'),
    staleTime: 5 * 60_000,
  });

  /**
   * Fetched here only for the count on the tab.
   *
   * Withdrawn ones included, because the tab counts what this screen manages
   * rather than what the till would offer.
   */
  const choices = useQuery({
    queryKey: ['menu', 'modifier-groups', 'all'],
    queryFn: () => api.get('/menu/modifier-groups?include_inactive=1'),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['menu'] });

  /**
   * Removing a dish or a category. The server decides what "remove" means and
   * says so: a dish never sold is deleted, one with sales is taken off the
   * menu instead, and a category still holding dishes is refused. Confirmed
   * first — it is the one button on this screen that throws work away.
   */
  const remove = useMutation({
    mutationFn: ({ kind, id }) => api.delete(`/menu/${kind}/${id}`),
    onSuccess: refresh,
    onError: (error) => window.alert(error?.message ?? 'That could not be removed.'),
  });

  const confirmRemove = (kind, row) => {
    const what = kind === 'categories' ? 'category' : 'dish';
    if (window.confirm(`Remove the "${row.name}" ${what}?`)) {
      remove.mutate({ kind, id: row.id });
    }
  };

  const lists = {
    products: products.data?.products ?? [],
    categories: categories.data?.categories ?? [],
    ingredients: ingredients.data?.ingredients ?? [],
  };

  return (
    <div className="mx-auto max-w-5xl p-4">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <h1 className="font-semibold text-xl text-ink">Menu</h1>

        {canEdit && tab === 'products' && (
          <Button onClick={() => setEditing({})}>Add a dish</Button>
        )}
        {canEdit && tab === 'categories' && (
          <Button onClick={() => setEditing({ category: true })}>Add a category</Button>
        )}
        {canEdit && tab === 'ingredients' && (
          <Button onClick={() => setEditingIngredient({})}>Add an ingredient</Button>
        )}
        {/* Offers bring their own button — the form is theirs, not this one's. */}
      </div>

      {/*
        Said plainly rather than by disabling buttons with no explanation.
        Somebody who came here to change a price needs to know where to go.
      */}
      {receivesMenu && (
        <p className="mb-4 rounded-lg bg-surface-3 px-3.5 py-2.5 text-sm text-ink-2">
          This branch receives its menu from head office. A change made here would be replaced at
          the next sync, so the menu is read-only — make the change on the head-office system and it
          will reach every branch.
        </p>
      )}

      {editing && (
        <EditPanel
          initial={editing}
          categories={lists.categories}
          onClose={() => setEditing(null)}
          onSaved={() => {
            refresh();
            setEditing(null);
          }}
        />
      )}

      {editingChoices && (
        <DishChoices
          product={editingChoices}
          onClose={() => setEditingChoices(null)}
          onSaved={() => {
            refresh();
            setEditingChoices(null);
          }}
        />
      )}

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'products', label: 'Dishes', count: lists.products.length },
          { id: 'categories', label: 'Categories', count: lists.categories.length },
          { id: 'ingredients', label: 'Ingredients', count: lists.ingredients.length },
          { id: 'recipes', label: 'Recipes', count: recipes.data?.recipes?.length ?? 0 },
          { id: 'choices', label: 'Choices', count: choices.data?.groups?.length ?? 0 },
          { id: 'offers', label: 'Offers', count: offers.data?.promotions?.length ?? 0 },
        ]}
      />

      <div className="mt-4">
        {tab === 'products' && (
          <DataTable
            rows={lists.products}
            loading={products.isLoading}
            error={products.isError}
            searchable
            searchKeys={['name', 'sku', 'category_name']}
            columns={[
              {
                key: 'name',
                label: 'Dish',
                tone: (row) => (row.is_active ? 'text-ink' : 'text-ink-3'),
              },
              { key: 'category_name', label: 'Category' },
              {
                key: 'is_active',
                label: 'On the menu',
                // A dish that has been taken off is the answer to half the
                // questions this screen gets asked.
                render: (row) => (row.is_active ? 'Yes' : 'Taken off'),
                tone: (row) => (row.is_active ? 'text-ink-2' : 'text-warn'),
              },
              {
                key: 'price',
                label: 'Price',
                align: 'right',
                render: (row) => formatPKR(row.price),
                tone: (row) => (row.is_active ? 'text-ink' : 'text-ink-3'),
              },
              ...(canEdit
                ? [
                    {
                      key: 'edit',
                      label: '',
                      align: 'right',
                      render: (row) => (
                        <span className="flex justify-end gap-3">
                          {/*
                            Sizes and extras are attached per dish, and this is
                            the only place it can be done — the table joining a
                            group to a dish had no endpoint that could write a
                            row, which is why nothing on this menu offers a
                            choice today.
                          */}
                          {row.is_active && (
                            <button
                              type="button"
                              onClick={() => setEditingChoices(row)}
                              className="text-sm text-ink-3 underline hover:text-ink"
                            >
                              Choices
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setEditing(row)}
                            className="text-sm text-ink-2 underline hover:text-ink"
                          >
                            {row.is_active ? 'Change' : 'Put back'}
                          </button>
                          <button
                            type="button"
                            onClick={() => confirmRemove('products', row)}
                            className="text-sm text-ink-3 underline hover:text-bad"
                          >
                            Remove
                          </button>
                        </span>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        )}

        {tab === 'categories' && (
          <DataTable
            rows={lists.categories}
            loading={categories.isLoading}
            error={categories.isError}
            columns={[
              { key: 'name', label: 'Category', tone: () => 'text-ink' },
              { key: 'product_count', label: 'Dishes', align: 'right' },
              ...(canEdit
                ? [
                    {
                      key: 'remove',
                      label: '',
                      align: 'right',
                      render: (row) => (
                        <button
                          type="button"
                          onClick={() => confirmRemove('categories', row)}
                          className="text-sm text-ink-3 underline hover:text-bad"
                        >
                          Remove
                        </button>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        )}

        {tab === 'choices' && <ChoicesTab canEdit={canEdit} />}

        {tab === 'offers' && <OffersTab canEdit={canEdit} />}

        {tab === 'recipes' && editingRecipe && (
          <RecipeForm
            product={editingRecipe}
            onClose={() => setEditingRecipe(null)}
            onSaved={() => {
              refresh();
              setEditingRecipe(null);
            }}
          />
        )}

        {tab === 'recipes' && (
          <DataTable
            rows={recipes.data?.recipes ?? []}
            loading={recipes.isLoading}
            error={recipes.isError}
            searchable
            searchKeys={['product_name', 'name']}
            columns={[
              { key: 'product_name', label: 'Dish', tone: () => 'text-ink' },
              {
                key: 'component_count',
                label: 'Ingredients',
                align: 'right',
                render: (row) => row.component_count,
              },
              {
                key: 'sells_for',
                label: 'Sells for',
                align: 'right',
                render: (row) => (row.sells_for ? formatPKR(row.sells_for) : '—'),
              },
              {
                key: 'costs_to_make',
                label: 'Costs',
                align: 'right',
                render: (row) => formatPKR(row.costs_to_make),
              },
              {
                key: 'share',
                label: 'Food cost',
                align: 'right',
                /*
                 * The column the screen exists for. Around thirty per cent
                 * leaves room for everything else; past forty the dish is
                 * eating the margin. Coloured rather than flagged, because a
                 * loss-leader is a legitimate choice and this is not the
                 * screen to argue with it.
                 */
                render: (row) =>
                  row.sells_for > 0
                    ? `${Math.round((row.costs_to_make / row.sells_for) * 100)}%`
                    : '—',
                tone: (row) => {
                  if (!(row.sells_for > 0)) return 'text-ink-3';
                  const share = (row.costs_to_make / row.sells_for) * 100;
                  return share > 40 ? 'text-bad' : share > 30 ? 'text-warn' : 'text-ink-2';
                },
              },
              {
                key: 'is_estimate',
                label: 'Where from',
                /*
                 * A recipe nobody has checked is a figure nobody should lean
                 * on, and the dashboard already says so in aggregate. This is
                 * where you find out which ones.
                 */
                render: (row) => (row.is_estimate ? 'Estimated' : 'Entered'),
                tone: (row) => (row.is_estimate ? 'text-warn' : 'text-ink-3'),
              },
              ...(canEditRecipes
                ? [
                    {
                      key: 'edit',
                      label: '',
                      align: 'right',
                      render: (row) => (
                        <button
                          type="button"
                          onClick={() =>
                            setEditingRecipe({
                              id: row.product_id,
                              name: row.product_name,
                              price: row.sells_for,
                            })
                          }
                          className="text-sm text-brand-400 hover:underline"
                        >
                          Change
                        </button>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        )}

        {tab === 'ingredients' && editingIngredient && (
          <IngredientForm
            initial={editingIngredient}
            onClose={() => setEditingIngredient(null)}
            onSaved={() => {
              refresh();
              setEditingIngredient(null);
            }}
          />
        )}

        {tab === 'ingredients' && (
          <DataTable
            rows={lists.ingredients}
            loading={ingredients.isLoading}
            error={ingredients.isError}
            searchable
            searchKeys={['name', 'sku', 'category']}
            columns={[
              { key: 'name', label: 'Ingredient', tone: () => 'text-ink' },
              { key: 'category', label: 'Category' },
              { key: 'unit_abbr', label: 'Unit' },
              {
                key: 'cost_per_unit',
                label: 'Cost',
                align: 'right',
                render: (row) => formatPKR(row.cost_per_unit),
              },
              {
                key: 'shelf',
                label: 'Goes off',
                // The gap made visible in the list rather than only inside
                // each record: a perishable with no shelf life can never be
                // warned about, and there are a hundred and twenty-one.
                render: (row) =>
                  row.is_perishable
                    ? row.shelf_life_days
                      ? `${row.shelf_life_days} days`
                      : 'not set'
                    : '—',
                tone: (row) =>
                  row.is_perishable && !row.shelf_life_days ? 'text-warn' : 'text-ink-2',
              },
              ...(canEdit
                ? [
                    {
                      key: 'edit',
                      label: '',
                      align: 'right',
                      render: (row) => (
                        <button
                          type="button"
                          onClick={() => setEditingIngredient(row)}
                          className="text-sm text-ink-2 underline hover:text-ink"
                        >
                          Change
                        </button>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        )}
      </div>
    </div>
  );
}

/** Adding or changing a dish, or adding a category. */
function EditPanel({ initial, categories, onClose, onSaved }) {
  const isCategory = initial.category === true;
  const isNew = !initial.id;

  const [name, setName] = useState(initial.name ?? '');
  const [categoryId, setCategoryId] = useState(initial.category_id ?? '');
  const [price, setPrice] = useState(initial.price != null ? String(initial.price) : '');
  const [onMenu, setOnMenu] = useState(
    initial.is_active === undefined ? true : !!initial.is_active
  );
  const [imageUrl, setImageUrl] = useState(initial.image_url ?? null);
  // What the dish is — the line that tells a cashier or waiter what a "Platter 1"
  // actually contains, shown on the till and the tablet.
  const [description, setDescription] = useState(initial.description ?? '');
  // Choices to offer, picked here while the dish is being added rather than in a
  // second trip through the Choices button. Only for a new dish; an existing one
  // keeps that button, which can also reorder them.
  const [chosenGroups, setChosenGroups] = useState([]);

  const choiceGroups = useQuery({
    queryKey: ['menu', 'modifier-groups'],
    queryFn: () => api.get('/menu/modifier-groups'),
    enabled: !isCategory && isNew,
  });
  const groups = choiceGroups.data?.groups ?? [];
  const toggleGroup = (id) =>
    setChosenGroups(
      chosenGroups.includes(id) ? chosenGroups.filter((g) => g !== id) : [...chosenGroups, id]
    );

  // --- Deals: a product made of slots. A slot is a quantity and the products
  //     that may fill it; one option is fixed, several is a choice at the till.
  const [isDeal, setIsDeal] = useState(initial.type === 'combo');
  // Each slot is { label, quantity, options: [{ productId, modifiers: [optionId] }] }.
  // An option's modifiers are the default size/extras it is set to for this deal,
  // pre-selected at the till and changeable there.
  const [dealSlots, setDealSlots] = useState([]);
  // When set, the ChoicePicker is open to choose an item's default choices for a
  // slot: { slotIndex, product, groups, initial }.
  const [configuring, setConfiguring] = useState(null);

  // The products that can fill a slot — every real dish that is not a deal
  // itself, nor this product. Loaded once for the picker, with each dish's
  // choices, so ticking a pizza can ask its size here and now.
  const dealPicker = useQuery({
    queryKey: ['menu', 'products', 'for-deal'],
    queryFn: () =>
      api.get(withQuery('/menu/products', { limit: 500, include_inactive: 1, with_modifiers: 1 })),
    enabled: !isCategory,
  });
  const pickable = (dealPicker.data?.products ?? []).filter(
    (p) => p.id !== initial.id && p.type !== 'combo'
  );
  const modifierGroups = dealPicker.data?.modifierGroups ?? {};
  const groupsFor = (productId) => modifierGroups[productId] ?? [];
  // The names of some chosen options for a product, in group order, for the line
  // that shows what a ticked item is set to (e.g. "13 Inch, Extra cheese").
  const optionNames = (productId, ids) => {
    if (!ids || ids.length === 0) return '';
    const names = new Map();
    for (const g of groupsFor(productId))
      for (const o of g.options ?? []) names.set(o.id, o.name);
    return ids.map((id) => names.get(id)).filter(Boolean).join(', ');
  };

  // The deal's slots as they stand, filled in when the panel opens. Each option
  // keeps its saved default choices so they show, and travel back, unchanged.
  const currentDeal = useQuery({
    queryKey: ['menu', 'deal', initial.id],
    queryFn: () => api.get(`/menu/products/${initial.id}/deal`),
    enabled: !isCategory && !isNew && initial.type === 'combo',
  });
  useEffect(() => {
    const slots = currentDeal.data?.deal?.slots;
    if (slots) {
      setDealSlots(
        slots.map((s) => ({
          label: s.label,
          quantity: s.quantity,
          options: s.options.map((o) => ({
            productId: o.productId,
            modifiers: o.defaultModifiers ?? [],
          })),
        }))
      );
    }
  }, [currentDeal.data]);

  const addSlot = () =>
    setDealSlots((rows) => [...rows, { label: '', quantity: 1, options: [] }]);
  const setSlot = (i, patch) =>
    setDealSlots((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeSlot = (i) => setDealSlots((rows) => rows.filter((_, idx) => idx !== i));

  // The option, if any, that a product currently fills in a slot.
  const optionIn = (slot, productId) => slot.options.find((o) => o.productId === productId);

  // Add or set an item's default choices in a slot (used after the ChoicePicker).
  const putOption = (i, productId, modifiers) =>
    setDealSlots((rows) =>
      rows.map((r, idx) => {
        if (idx !== i) return r;
        const rest = r.options.filter((o) => o.productId !== productId);
        return { ...r, options: [...rest, { productId, modifiers }] };
      })
    );
  const removeOption = (i, productId) =>
    setDealSlots((rows) =>
      rows.map((r, idx) =>
        idx === i ? { ...r, options: r.options.filter((o) => o.productId !== productId) } : r
      )
    );

  // Ticking an item: if it has choices, ask them here (as a default for the
  // deal); if it has none, it just goes in. Unticking takes it out.
  const toggleOption = (i, product) => {
    if (optionIn(dealSlots[i], product.id)) {
      removeOption(i, product.id);
      return;
    }
    const groups = groupsFor(product.id);
    if (groups.length === 0) {
      putOption(i, product.id, []);
      return;
    }
    setConfiguring({ slotIndex: i, product, groups, initial: null });
  };

  // The "change" affordance on a ticked item — reopen its choices, pre-filled.
  const changeOption = (i, product) => {
    const groups = groupsFor(product.id);
    if (groups.length === 0) return;
    const current = optionIn(dealSlots[i], product.id);
    setConfiguring({ slotIndex: i, product, groups, initial: current?.modifiers ?? [] });
  };

  // Turning a product into a deal with no slots yet gives it one to fill, rather
  // than a checkbox that seems to do nothing.
  const enableDeal = (on) => {
    setIsDeal(on);
    if (on && dealSlots.length === 0) setDealSlots([{ label: '', quantity: 1, options: [] }]);
  };

  const save = useMutation({
    mutationFn: async () => {
      if (isCategory) return api.post('/menu/categories', { name: name.trim() });

      const body = {
        name: name.trim(),
        categoryId: categoryId || null,
        // Blank on a new dish means nothing was typed, which is a price of zero —
        // an intended one, priced by its choices. Blank on an edit means leave
        // the price as it was.
        price: price === '' ? (isNew ? 0 : undefined) : Number(price),
        description: description.trim(),
        isActive: onMenu,
        imageUrl,
      };

      let productId = initial.id;
      if (isNew) {
        const created = await api.post('/menu/products', body);
        productId = created.id;
        // Attach the choices picked above, in the order they were shown.
        if (chosenGroups.length) {
          await api.put(`/menu/products/${productId}/modifier-groups`, { groupIds: chosenGroups });
        }
      } else {
        await api.put(`/menu/products/${productId}`, body);
      }

      // The deal's slots: set the whole set, or clear it if this stopped being a
      // deal. Done after the product exists, since it hangs off its id.
      const slots = dealSlots
        .map((s) => ({
          label: s.label.trim(),
          quantity: Math.max(1, Number(s.quantity) || 1),
          // Each option carries its default choices for the deal.
          options: s.options
            .filter((o) => o.productId)
            .map((o) => ({ productId: o.productId, modifiers: o.modifiers ?? [] })),
        }))
        .filter((s) => s.label && s.options.length > 0);
      if (isDeal && slots.length) {
        await api.put(`/menu/products/${productId}/deal`, { slots });
      } else if (!isDeal && initial.type === 'combo') {
        await api.delete(`/menu/products/${productId}/deal`);
      }

      return { id: productId };
    },
    onSuccess: onSaved,
  });

  // A price may be zero (a dish priced by its choices), but never negative or a
  // non-number.
  const priceIsValid = price === '' || (Number.isFinite(Number(price)) && Number(price) >= 0);
  // A deal needs at least one slot with a name and something to fill it.
  const dealReady = !isDeal || dealSlots.some((s) => s.label.trim() && s.options.length > 0);
  const ready = name.trim() && (isCategory || !isNew || priceIsValid) && dealReady;

  return (
    <div className="mb-5 rounded-xl border border-rule bg-surface p-5">
      <h2 className="mb-3 font-medium text-ink">
        {isCategory ? 'New category' : isNew ? 'New dish' : name || 'Change this dish'}
      </h2>

      <div className="grid gap-3 sm:grid-cols-2">
        <label>
          <span className="mb-1.5 block text-xs text-ink-3">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none"
          />
        </label>

        {!isCategory && (
          <>
            <label>
              <span className="mb-1.5 block text-xs text-ink-3">Category</span>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none"
              >
                <option value="">None</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span className="mb-1.5 block text-xs text-ink-3">
                Price{' '}
                {!isNew ? (
                  <span className="text-ink-3">— leave blank to keep it</span>
                ) : (
                  <span className="text-ink-3">— 0 is fine if the choices set it</span>
                )}
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-right text-sm tabular-nums text-ink focus:border-brand-400 focus:outline-none"
              />
              {!priceIsValid && (
                <span className="mt-1 block text-xs text-warn">
                  A price cannot be less than nothing.
                </span>
              )}
            </label>

            <label className="flex items-end gap-2 pb-2">
              <input
                type="checkbox"
                checked={onMenu}
                onChange={(e) => setOnMenu(e.target.checked)}
                className="h-5 w-5 rounded border-rule accent-brand-400"
              />
              <span className="text-sm text-ink-2">On the menu</span>
            </label>

            <label className="sm:col-span-2">
              <span className="mb-1.5 block text-xs text-ink-3">
                Description{' '}
                <span className="text-ink-3">
                  — what it contains, shown on the till and the tablet
                </span>
              </span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder="e.g. 2 pcs broast, fries, coleslaw and a drink"
                className="w-full rounded-lg border border-rule bg-surface-3 px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand-400 focus:outline-none"
              />
            </label>

            <div className="sm:col-span-2">
              <ImagePicker value={imageUrl} onChange={setImageUrl} />
            </div>

            {/* Choices to ask for, picked while adding the dish. Only for a new
                dish — an existing one uses the Choices button, which can also
                reorder them. */}
            {isNew && groups.length > 0 && (
              <div className="sm:col-span-2">
                <span className="mb-1.5 block text-xs text-ink-3">
                  Choices to ask for when this is ordered
                </span>
                <div className="space-y-1.5 rounded-lg border border-rule bg-surface-3 p-3">
                  {groups.map((group) => (
                    <label
                      key={group.id}
                      className="flex flex-wrap items-center gap-2.5 text-sm text-ink-2"
                    >
                      <input
                        type="checkbox"
                        aria-label={`Offer ${group.name}`}
                        checked={chosenGroups.includes(group.id)}
                        onChange={() => toggleGroup(group.id)}
                        className="h-4 w-4"
                      />
                      <span className="text-ink">{group.name}</span>
                      <span className="text-xs text-ink-3">{describeRules(group)}</span>
                      <span className="text-xs text-ink-3">
                        {(group.options ?? []).map((o) => o.name).join(', ')}
                      </span>
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-xs text-ink-3">
                  You can add, remove or reorder these later with the Choices button.
                </p>
              </div>
            )}

            {/* A deal: this product is made of parts (slots). Each part is a
                quantity and the items that may fill it — one item is included
                automatically, several is a choice the till asks the customer. */}
            <div className="sm:col-span-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={isDeal}
                  onChange={(e) => enableDeal(e.target.checked)}
                  className="h-5 w-5 rounded border-rule accent-brand-400"
                />
                <span className="text-sm text-ink-2">This is a deal — it is made of several items</span>
              </label>

              {isDeal && (
                <div className="mt-2 space-y-3">
                  {dealSlots.map((slot, i) => (
                    <div key={i} className="space-y-2 rounded-lg border border-rule bg-surface-3 p-3">
                      <div className="flex items-center gap-2">
                        <input
                          value={slot.label}
                          onChange={(e) => setSlot(i, { label: e.target.value })}
                          placeholder="Part name — e.g. Pizza"
                          aria-label={`Part ${i + 1} name`}
                          className="h-10 min-w-0 flex-1 rounded-lg border border-rule bg-surface px-2 text-sm text-ink focus:border-brand-400 focus:outline-none"
                        />
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={slot.quantity}
                          onChange={(e) => setSlot(i, { quantity: e.target.value })}
                          aria-label={`How many for part ${i + 1}`}
                          className="h-10 w-16 rounded-lg border border-rule bg-surface px-2 text-right text-sm tabular-nums text-ink focus:border-brand-400 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => removeSlot(i)}
                          aria-label={`Remove part ${i + 1}`}
                          className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-ink-2 hover:bg-surface"
                        >
                          Remove
                        </button>
                      </div>

                      <div>
                        <span className="mb-1 block text-xs text-ink-3">
                          Which items can fill it
                          {slot.options.length > 1 ? ' — the till asks the customer to choose' : ''}
                        </span>

                        {/* Narrow the menu down — a Pizza part wants the pizzas,
                            not all of the menu at once. */}
                        <div className="mb-1.5 flex gap-2">
                          <input
                            value={slot.search ?? ''}
                            onChange={(e) => setSlot(i, { search: e.target.value })}
                            placeholder="Search items"
                            aria-label={`Search items for part ${i + 1}`}
                            className="h-9 min-w-0 flex-1 rounded-lg border border-rule bg-surface px-2 text-sm text-ink focus:border-brand-400 focus:outline-none"
                          />
                          <select
                            value={slot.category ?? ''}
                            onChange={(e) => setSlot(i, { category: e.target.value })}
                            aria-label={`Category for part ${i + 1}`}
                            className="h-9 rounded-lg border border-rule bg-surface px-2 text-sm text-ink focus:border-brand-400 focus:outline-none"
                          >
                            <option value="">All categories</option>
                            {categories.map((c) => (
                              <option key={c.id} value={c.name}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-rule bg-surface p-2">
                          {(() => {
                            const q = (slot.search ?? '').trim().toLowerCase();
                            const cat = slot.category ?? '';
                            const shown = pickable.filter(
                              (p) =>
                                // A ticked item stays visible so it can always be
                                // unticked, even when the filter would hide it.
                                optionIn(slot, p.id) ||
                                ((!q || p.name.toLowerCase().includes(q)) &&
                                  (!cat || p.category_name === cat))
                            );
                            if (shown.length === 0) {
                              return (
                                <p className="px-1 py-2 text-xs text-ink-3">Nothing matches.</p>
                              );
                            }
                            return shown.map((p) => {
                              const picked = optionIn(slot, p.id);
                              const hasChoices = groupsFor(p.id).length > 0;
                              const chosen = picked ? optionNames(p.id, picked.modifiers) : '';
                              return (
                                <div key={p.id} className="text-sm text-ink-2">
                                  <div className="flex items-center gap-2">
                                    <label className="flex flex-1 items-center gap-2">
                                      <input
                                        type="checkbox"
                                        aria-label={p.name}
                                        checked={Boolean(picked)}
                                        onChange={() => toggleOption(i, p)}
                                        className="h-4 w-4"
                                      />
                                      <span className="text-ink">{p.name}</span>
                                      {p.category_name ? (
                                        <span className="text-xs text-ink-3">
                                          {p.category_name}
                                        </span>
                                      ) : null}
                                    </label>
                                    {picked && hasChoices ? (
                                      <button
                                        type="button"
                                        onClick={() => changeOption(i, p)}
                                        className="shrink-0 text-xs text-brand-400 underline hover:text-brand-300"
                                      >
                                        {chosen ? 'Change' : 'Set choices'}
                                      </button>
                                    ) : null}
                                  </div>
                                  {/* What this item is set to inside the deal — the
                                      default the till opens on, changeable there. */}
                                  {picked && hasChoices && chosen ? (
                                    <p className="ml-6 text-xs text-ink-3">{chosen}</p>
                                  ) : null}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addSlot}
                    className="text-sm font-medium text-brand-400 hover:text-brand-300"
                  >
                    + Add a part
                  </button>
                  <p className="text-xs text-ink-3">
                    The deal is charged at the price above. A part with one item is included
                    automatically; a part with several is a choice at the till.
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {save.isError && (
        <p className="mt-3 rounded-lg bg-bad-bg px-3 py-2 text-sm text-bad">
          {save.error?.message ?? 'That did not work.'}
        </p>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={() => save.mutate()} disabled={!ready || save.isPending}>
          {save.isPending ? 'Saving' : 'Save'}
        </Button>
      </div>

      {/* Choosing an item's default size/extras for this deal. Prices are hidden
          — the deal is one price — and it saves the choice rather than adding to
          an order. The till opens on this default and can change it. */}
      {configuring && (
        <ChoicePicker
          product={configuring.product}
          groups={configuring.groups}
          hidePrices
          confirmLabel="Save these choices"
          initial={configuring.initial}
          onCancel={() => setConfiguring(null)}
          onDone={(modifiers) => {
            putOption(
              configuring.slotIndex,
              configuring.product.id,
              modifiers.map((m) => m.optionId)
            );
            setConfiguring(null);
          }}
        />
      )}
    </div>
  );
}
